import React, { useCallback, useEffect, useState } from 'react';
import { Box, InputAdornment, MenuItem, TextField } from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { selectCurrentUser, type AuthUser } from '../../../../redux/slices/auth.slice';
import { sellersService, type SellerByLocation } from '../../../../services/sellers.service';
import type { SelectedSeller } from '../../../../types/sales.types';

export interface SaleSellerSelectProps {
  locationId: number | undefined;
  value: SelectedSeller | null;
  onChange: (seller: SelectedSeller | null) => void;
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
}

const SaleSellerSelect: React.FC<SaleSellerSelectProps> = ({
  locationId,
  value,
  onChange,
  required = false,
  disabled = false,
  helperText,
}) => {
  const currentUser = useSelector(selectCurrentUser) as AuthUser | null;
  const [sellers, setSellers] = useState<SellerByLocation[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (locationId == null) {
      setSellers([]);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    void sellersService
      .getByLocation(locationId)
      .then((data: SellerByLocation[]) => {
        if (!cancelled) setSellers(data);
      })
      .catch(() => {
        if (!cancelled) setSellers([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [locationId]);

  const handleChange = useCallback(
    (sellerId: string) => {
      if (sellerId === '') {
        onChange(null);
        return;
      }
      if (currentUser && sellerId === currentUser.id) {
        onChange({
          id: currentUser.id,
          firstName: currentUser.firstName ?? '',
          lastName: currentUser.lastName ?? '',
          email: currentUser.email ?? '',
        });
        return;
      }
      const seller = sellers.find((row) => row.id === sellerId);
      if (seller != null) {
        onChange({
          id: seller.id,
          firstName: seller.firstName,
          lastName: seller.lastName,
          email: seller.email,
        });
        return;
      }
      // Keep hydrated seller if MUI fires change for an orphan option we rendered.
      if (value != null && value.id === sellerId) {
        return;
      }
      onChange(null);
    },
    [currentUser, onChange, sellers, value],
  );

  const valueInList =
    value != null &&
    (value.id === currentUser?.id || sellers.some((seller) => seller.id === value.id));
  const orphanSeller = value != null && !valueInList ? value : null;

  return (
    <Box>
      <TextField
        select
        fullWidth
        size="small"
        label="Vendedor"
        value={value?.id ?? ''}
        onChange={(event) => handleChange(event.target.value)}
        disabled={disabled || isLoading || locationId == null}
        required={required}
        helperText={
          helperText ??
          (required && value == null ? 'Seleccione un vendedor para continuar.' : undefined)
        }
        SelectProps={{ displayEmpty: true }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PersonIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
      >
        <MenuItem value="" disabled>
          Seleccione un vendedor
        </MenuItem>
        {orphanSeller != null && (
          <MenuItem value={orphanSeller.id}>
            {orphanSeller.firstName} {orphanSeller.lastName}
          </MenuItem>
        )}
        {currentUser && (
          <MenuItem value={currentUser.id}>
            {currentUser.firstName} {currentUser.lastName} (yo)
          </MenuItem>
        )}
        {sellers
          .filter((seller) => seller.id !== currentUser?.id)
          .map((seller) => (
            <MenuItem key={seller.id} value={seller.id}>
              {seller.firstName} {seller.lastName}
            </MenuItem>
          ))}
      </TextField>
    </Box>
  );
};

export default SaleSellerSelect;
